import {
  Body,
  Button,
  Heading,
  Icon,
  IconButton,
} from '@assembly-js/design-system';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import clsx from 'clsx';
import { Formik, FormikProps } from 'formik';
import moment from 'moment';
import { useRef, useState } from 'react';
import { z } from 'zod';
import { toFormikValidate } from 'zod-formik-adapter';

import { useAppSelector } from 'src/hooks/useStore';
import ActionButtonModal from 'src/legacy/components/Modals/ActionButtonModal';
import { Note } from 'src/services/api/notesApi';

import { ActionMenu } from './ActionMenu';
import { MobileInfoPopover } from './MobileInfoPopover';
import { NoteEditor } from './NoteEditor';

const MAX_TITLE_LENGTH = 150;
const MAX_CONTENT_LENGTH = 25000;

const noteSchema = z.object({
  title: z
    .string()
    .min(1, { message: 'You must set a title' })
    .max(MAX_TITLE_LENGTH, {
      message: `The title can be no longer than ${MAX_TITLE_LENGTH} characters`,
    })
    .trim()
    .refine(Boolean, {
      message: 'Title cannot be empty',
    }),
  content: z.string().max(MAX_CONTENT_LENGTH, {
    message: `The content can be no longer than ${MAX_CONTENT_LENGTH} characters`,
  }),
});

type NoteFormValues = z.infer<typeof noteSchema>;

const CreatedByInfo = ({
  creatorId,
  createdAt,
  className,
}: Pick<Note, 'creatorId' | 'createdAt'> & { className?: string }) => {
  const users = useAppSelector((state) => state.users.internalUsers);
  const user = users.find((u) => u.id === creatorId);
  const userName =
    user?.fields.givenName && user?.fields.familyName
      ? `${user.fields.givenName} ${user.fields.familyName}`
      : null;
  return (
    <Body className={className} size="xs">
      {`Created ${userName ? `by ${userName}` : ''} ${moment(
        createdAt,
      ).fromNow()}`}
    </Body>
  );
};

export interface NoteModalHandlerProps {
  onDelete: (note: Note) => void;
  onSave: (title: string, content: string, note: Note | null) => void;
  onClose: () => void;
}

export interface NoteModalStateProps {
  note: Note | null;
  open: boolean;
}

export const NoteModal = ({
  open,
  onClose,
  onDelete,
  onSave,
  note,
}: NoteModalHandlerProps & NoteModalStateProps) => {
  const isEdit = note !== null;
  const plaintextContent = useRef('');
  const [submitted, setSubmitted] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);
  const formikRef = useRef<FormikProps<NoteFormValues> | null>(null);
  // If the user has unsaved changes, we need to show a confirmation modal
  const handleClose = () => {
    if (formikRef.current && !formikRef.current.dirty) {
      onClose();
    } else {
      setShowConfirmation(true);
    }
  };
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        classes={{ paper: 'w-full sm:w-4/5' }}
        // hide the edit note dialog when the skip confirmation dialog is open.
        // As it is not recommended to show stacked dialogs.
        hidden={showConfirmation}
        transitionDuration={showConfirmation ? 0 : 100}
        // Disable focus enforcement when link popover is open to allow focus in the popover
        disableEnforceFocus={isLinkPopoverOpen}
      >
        <DialogTitle classes={{ root: 'px-5 py-3' }}>
          <div className="flex items-center justify-between">
            <Heading size="lg">{isEdit ? 'Edit note' : 'Create note'}</Heading>
            <IconButton
              icon="Close"
              label="Close"
              variant="minimal"
              onClick={onClose}
              className="hover:cursor-pointer w-6 h-6 p-0 flex items-center justify-center *:w-4 *:h-4"
            />
          </div>
        </DialogTitle>
        <Formik
          innerRef={formikRef}
          initialValues={{
            title: isEdit ? note.title : '',
            content: isEdit ? note.content : '',
          }}
          validate={(values) =>
            toFormikValidate(noteSchema)({
              ...values,
              content: plaintextContent.current,
            })
          }
          validateOnChange
          onSubmit={({ title, content }) => {
            onSave(title, content, note);
            onClose();
          }}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit}>
              <DialogContent dividers classes={{ root: 'p-5' }}>
                <NoteEditor
                  title={formik.values.title}
                  onTitleChange={formik.handleChange}
                  content={formik.values.content}
                  onContentChange={({ html, plaintext, event }) => {
                    if (event === 'update') {
                      formik.setFieldValue('content', html);
                    }
                    plaintextContent.current = plaintext;
                  }}
                  onLinkPopoverOpenChange={setIsLinkPopoverOpen}
                />
                {!formik.isValid && submitted && (
                  <div className="mt-1 flex items-center gap-1 text-[#991A00]">
                    <Icon icon="Warning" className="w-3 h-3" />
                    <Body size="sm" tag="span">
                      {`${formik.errors.title ?? formik.errors.content}`}
                    </Body>
                  </div>
                )}
              </DialogContent>
              <DialogActions
                classes={{
                  root: clsx(
                    'flex justify-between gap-4 px-4 py-5',
                    !isEdit && 'flex-row-reverse',
                  ),
                }}
              >
                {isEdit && (
                  <>
                    <div className="hidden sm:block">
                      <CreatedByInfo
                        creatorId={note.creatorId}
                        createdAt={note.createdAt}
                        className="text-gray-small"
                      />
                    </div>
                    <div className="block sm:hidden">
                      <MobileInfoPopover>
                        <CreatedByInfo
                          creatorId={note.creatorId}
                          createdAt={note.createdAt}
                        />
                      </MobileInfoPopover>
                    </div>
                  </>
                )}
                <div className="flex items-center gap-4">
                  {isEdit && <ActionMenu onDelete={() => onDelete(note)} />}
                  <Button
                    onClick={handleClose}
                    label="Cancel"
                    variant="secondary"
                  />
                  <Button
                    onClick={() => setSubmitted(true)}
                    label={isEdit ? 'Save' : 'Create'}
                    disabled={!formik.dirty}
                    type="submit"
                  />
                </div>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>

      <ActionButtonModal
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        title="Unsaved changes"
        mainComponent="If you leave this page, your changes will be lost."
        cancelActionText="Cancel"
        positiveActionText="Leave page"
        positiveAction
        onPositiveActionPerformed={() => {
          onClose();
          setShowConfirmation(false);
        }}
      />
    </>
  );
};
